import { submissionAPI, wechatAPI } from '../api'
import { getWeChatOpenId, saveWeChatAuthFromUrl, persistOpenId, readOpenIdFromCookie, readLastSubmissionId, isBrowseMode, enterBrowseMode, clearBrowseMode } from './wechat'
import { isLocalDevHost, isReviewPreviewPath } from './devMode'

export { persistOpenId, readOpenIdFromCookie, isBrowseMode, enterBrowseMode, clearBrowseMode }

/** 微信二次进入：URL → Cookie → 服务端 Cookie API → localStorage */
export async function resolveOpenIdAsync() {
  saveWeChatAuthFromUrl()

  const fromUrl = new URLSearchParams(window.location.search).get('openid')
  if (fromUrl) {
    persistOpenId(fromUrl)
    return fromUrl
  }

  const fromCookie = readOpenIdFromCookie()
  if (fromCookie) {
    persistOpenId(fromCookie)
    return fromCookie
  }

  try {
    const res = await wechatAPI.getCurrentOpenId()
    const oid = res.data?.OpenId || res.data?.openId
    if (oid) {
      persistOpenId(oid)
      return oid
    }
  } catch (_) { /* 404 */ }

  const stored = getWeChatOpenId()
  if (stored) return stored

  return ''
}

export async function ensureOpenIdAsync(submissionId) {
  let openId = await resolveOpenIdAsync()
  if (openId) return openId

  if (!submissionId) return ''

  try {
    const res = await submissionAPI.getDetail(submissionId)
    const uid = res.data?.UserId?.trim()
    if (uid) {
      persistOpenId(uid, res.data?.Id)
      return uid
    }
  } catch (_) { /* ignore */ }

  return ''
}

export const getSubmissionStatus = (data) => Number(data?.Status ?? data?.status ?? -1)

export const routeBySubmission = (data) => {
  if (!data?.Id) return null
  const status = getSubmissionStatus(data)
  if (status === 0) return `/review-pending/${data.Id}`
  if (status === 1) return `/review-success/${data.Id}`
  if (status === 2) return `/review-rejected/${data.Id}`
  return null
}

/** 浏览模式：用户主动返回首页/重新上传时，不再自动跳进审核结果页 */
const applyBrowseModeFilter = (path, data) => {
  if (!path || !isBrowseMode()) return path
  const status = data ? getSubmissionStatus(data) : -1
  if (status === 1 || path.startsWith('/review-success')) return null
  if (status === 2 || path.startsWith('/review-rejected')) return null
  if (status === 0 || path.startsWith('/review-pending')) return null
  return path
}

const pick = (data, ...keys) => {
  for (const key of keys) {
    if (data?.[key] !== undefined && data?.[key] !== null) return data[key]
  }
  return undefined
}

export const mapEntryToRoute = (data) => {
  if (!data) return null

  const needOpenId = pick(data, 'NeedOpenId', 'needOpenId')
  const submissionId = pick(data, 'SubmissionId', 'submissionId')
  const submissionStatus = pick(data, 'SubmissionStatus', 'submissionStatus')
  const page = pick(data, 'Page', 'page')

  const path = routeBySubmission({ Id: submissionId, Status: submissionStatus })
  if (path) return { path }

  if (submissionId && page && page !== 'home') {
    const pagePaths = {
      'review-pending': `/review-pending/${submissionId}`,
      'review-success': `/review-success/${submissionId}`,
      'review-rejected': `/review-rejected/${submissionId}`
    }
    if (pagePaths[page]) return { path: pagePaths[page] }
  }

  if (needOpenId) {
    return {
      needOpenId: true,
      authorizeUrl: pick(data, 'AuthorizeUrl', 'authorizeUrl')
    }
  }

  return null
}

export const resolveLatestRouteByOpenId = async (openId) => {
  try {
    const res = await submissionAPI.getMyLatestStatus()
    return applyBrowseModeFilter(routeBySubmission(res.data), res.data)
  } catch (err) {
    if (err?.response?.status !== 404) {
      console.error('查询当前用户最新提交失败:', err)
    }
  }

  if (!openId) return null

  try {
    const res = await submissionAPI.getStatusByUser(openId)
    return applyBrowseModeFilter(routeBySubmission(res.data), res.data)
  } catch (err) {
    if (err?.response?.status !== 404) {
      console.error('查询用户最新提交状态失败:', err)
    }
  }

  return null
}

/** 用上次提交 Id 反查（不依赖 openid，最稳的二次进入兜底） */
export const resolveFromLastSubmission = async () => {
  const lastId = readLastSubmissionId()
  if (!lastId) return null

  try {
    const res = await submissionAPI.getDetail(lastId)
    const detail = res.data
    if (!detail?.Id) return null
    if (detail.UserId) persistOpenId(detail.UserId, detail.Id)
    return applyBrowseModeFilter(routeBySubmission(detail), detail)
  } catch (_) { /* ignore */ }

  return null
}

const syncOpenIdFromEntryResponse = (data) => {
  const serverOpenId = pick(data, 'OpenId', 'openId')
  if (serverOpenId) {
    const submissionId = pick(data, 'SubmissionId', 'submissionId')
    persistOpenId(String(serverOpenId).trim(), submissionId)
    return String(serverOpenId).trim()
  }
  return ''
}

/**
 * 解析当前用户应跳转的路径（不执行跳转）
 * 优先级：服务端最新提交 → 上次提交 Id → resolve-entry
 */
export const resolveTargetPath = async (returnPath = '/') => {
  // 1. 服务端 Cookie/请求头识别 openid，查最新一条（UpdatedAt DESC）
  const byServer = await resolveLatestRouteByOpenId(await resolveOpenIdAsync())
  if (byServer) return byServer

  // 2. 上次提交 Id 兜底（微信清缓存后）
  const fromLast = await resolveFromLastSubmission()
  if (fromLast) return fromLast

  // 3. 后端 resolve-entry
  try {
    let appId = ''
    try {
      appId = localStorage.getItem('wechat_app_id') || ''
    } catch (_) { /* ignore */ }

    if (!appId) {
      const cfg = await wechatAPI.getPublicConfig()
      appId = pick(cfg.data, 'appId', 'AppId') || ''
      if (appId) {
        try { localStorage.setItem('wechat_app_id', appId) } catch (_) { /* ignore */ }
      }
    }

    const openId = await resolveOpenIdAsync()
    const res = await wechatAPI.resolveEntry(openId || '', appId, returnPath)
    const recovered = syncOpenIdFromEntryResponse(res.data)
    const activeOpenId = recovered || openId

    const mapped = mapEntryToRoute(res.data)
    if (mapped?.path) {
      const status = pick(res.data, 'SubmissionStatus', 'submissionStatus')
      return applyBrowseModeFilter(mapped.path, { Status: status })
    }

    const retry = await resolveLatestRouteByOpenId(activeOpenId)
    if (retry) return retry
  } catch (err) {
    console.error('解析入口页面失败:', err)
  }

  return null
}

/** @deprecated 使用 resolveTargetPath */
export const resolveEntryRoute = async (openId, returnPath = '/') => {
  const path = await resolveTargetPath(returnPath)
  if (path) return { path }
  const oid = openId || await resolveOpenIdAsync()
  return oid ? { path: null } : { needOpenId: true }
}

export const redirectByEntryStatus = async (returnPath = '/') => {
  const target = await resolveTargetPath(returnPath)
  if (target && window.location.pathname !== target) {
    window.location.replace(target)
    return true
  }
  return false
}

export const shouldSkipEntryResolve = (path) => {
  if (path.startsWith('/admin')) return true
  if (path.startsWith('/open-in-wechat')) return true
  if (isLocalDevHost() && (path.startsWith('/review-') || isReviewPreviewPath(path))) return true
  return false
}

export const isEntryLandingPath = (path) => {
  if (path === '/') return true
  if (/^\/task\/\d+$/.test(path)) return true
  return false
}

/** @deprecated 使用 isBrowseMode */
export const isForceHomeView = isBrowseMode

/** @deprecated 使用 clearBrowseMode */
export const clearForceHomeView = clearBrowseMode

export const navigateToHome = () => {
  enterBrowseMode()
  window.location.href = '/'
}
